import React, { useMemo, useState, useRef, useEffect } from "react";
import { animate, stagger } from "animejs";
import { useTranslation } from "react-i18next";
import type {
  StateInterval,
  StateCategory,
  MachineSummaryRow,
  FaultEvent,
} from "../../services/ShiftAnalysisService";
import { STATE_COLORS, STATE_ORDER, formatTimeOfDay, parseTs } from "./utils";
import Panel from "./Panel";

interface Props {
  stateLanes: Map<string, StateInterval[]>;
  machines: MachineSummaryRow[];
  /** Fault events per machine (PC.shift_timeline) — used to enrich the readout. */
  faultLanes?: Map<string, FaultEvent[]>;
  focusedSn?: string | null;
  onHover?: (sn: string | null) => void;
  onPin?: (sn: string) => void;
}

interface Cursor {
  frac: number; // 0..1 across the track region
  t: number; // wall-clock-anchored ms
  clientX: number;
  clientY: number;
}

const LANE_H = 30;
const LANE_GAP = 6;
const LABEL_W = 150;

/** Format a wall-clock-anchored ms back to HH:MM:SS. */
function fmtMs(t: number): string {
  const d = new Date(t);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}

const StatusTimeline: React.FC<Props> = ({
  stateLanes,
  machines,
  faultLanes,
  focusedSn,
  onHover,
  onPin,
}) => {
  const { t } = useTranslation();
  const [cursor, setCursor] = useState<Cursor | null>(null);
  const barsRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const { start, end, ticks } = useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const ivs of stateLanes.values()) {
      for (const iv of ivs) {
        const s = parseTs(iv.state_start);
        const e = parseTs(iv.state_end);
        if (!Number.isNaN(s)) lo = Math.min(lo, s);
        if (!Number.isNaN(e)) hi = Math.max(hi, e);
      }
    }
    if (!Number.isFinite(lo) || !Number.isFinite(hi))
      return { start: 0, end: 0, ticks: [] as number[] };
    const HOUR = 3600_000;
    const s = Math.floor(lo / HOUR) * HOUR;
    const e = Math.ceil(hi / HOUR) * HOUR;
    const span = e - s;
    const step =
      span <= 4 * HOUR ? HOUR : span <= 10 * HOUR ? 2 * HOUR : 3 * HOUR;
    const tk: number[] = [];
    for (let t0 = s; t0 <= e; t0 += step) tk.push(t0);
    return { start: s, end: e, ticks: tk };
  }, [stateLanes]);

  const span = end - start;

  const lanesOrdered = useMemo(() => {
    const out: { sn: string; name: string }[] = [];
    const seen = new Set<string>();
    for (const m of machines) {
      if (stateLanes.has(m.machine_sn)) {
        out.push({ sn: m.machine_sn, name: m.machine_name });
        seen.add(m.machine_sn);
      }
    }
    for (const [sn, ivs] of stateLanes) {
      if (!seen.has(sn)) {
        out.push({ sn, name: ivs[0]?.machine_name ?? sn });
        seen.add(sn);
      }
    }
    return out;
  }, [machines, stateLanes]);

  // Synchronized readout: each machine's state + active fault at the cursor time.
  const readout = useMemo(() => {
    if (!cursor) return [];
    return lanesOrdered.map((lane) => {
      const ivs = stateLanes.get(lane.sn) ?? [];
      const iv = ivs.find(
        (x) =>
          parseTs(x.state_start) <= cursor.t && parseTs(x.state_end) > cursor.t,
      );
      let fault: string | undefined;
      if (iv && iv.state_category !== "running" && faultLanes) {
        const evs = faultLanes.get(lane.sn) ?? [];
        const active = evs
          .filter(
            (e) =>
              parseTs(e.event_start) <= cursor.t &&
              parseTs(e.event_end) > cursor.t,
          )
          .sort((a, b) => b.duration_s - a.duration_s);
        fault = active[0]?.fault_text;
      }
      return {
        sn: lane.sn,
        name: lane.name,
        category: iv?.state_category,
        fault,
      };
    });
  }, [cursor, lanesOrdered, stateLanes, faultLanes]);

  // The bars grow in when the timeline appears and when it shows another
  // window (another shift), not when its data refreshes: a line's Live Shift
  // rebuilds the lanes every few seconds, and keying the animation on them
  // made the whole chart grow in again each time. Keyed on primitives, so a
  // refresh neither restarts it nor (through the cleanup) stops it half-way.
  const hasBars = span > 0 && lanesOrdered.length > 0;
  useEffect(() => {
    if (!hasBars) return;
    const root = barsRef.current;
    if (!root) return;
    const bars = root.querySelectorAll(".sa-st-bar");
    if (bars.length === 0) return;
    const ctrl = animate(bars, {
      scaleX: [0, 1],
      duration: 700,
      delay: stagger(2, { start: 50 }),
      ease: "outExpo",
    });
    return () => {
      try {
        (ctrl as unknown as { pause?: () => void }).pause?.();
      } catch {
        /* noop */
      }
    };
  }, [start, hasBars]);

  const handleMove = (e: React.MouseEvent) => {
    const el = overlayRef.current;
    if (!el || span <= 0) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const frac = rect.width > 0 ? x / rect.width : 0;
    const tMs = start + frac * span;
    setCursor({ frac, t: tMs, clientX: e.clientX, clientY: e.clientY });
    const y = e.clientY - rect.top;
    const idx = Math.floor(y / (LANE_H + LANE_GAP));
    onHover?.(lanesOrdered[idx]?.sn ?? null);
  };

  const handleLeave = () => {
    setCursor(null);
    onHover?.(null);
  };

  if (span <= 0 || lanesOrdered.length === 0) {
    return (
      <div
        className="rounded-2xl border p-8 text-center"
        style={{
          backgroundColor: "var(--bg-primary)",
          borderColor: "var(--border-primary)",
        }}
      >
        <span className="text-sm" style={{ color: "var(--text-secondary)" }}>
          {t("noStateData", "No operating-state data for this shift.")}
        </span>
      </div>
    );
  }

  const tracksHeight = lanesOrdered.length * (LANE_H + LANE_GAP);

  return (
    <Panel
      label={t("operatingTimeline", "Operating timeline")}
      meta={t("whatEachMachineWasDoing", "what each machine was doing")}
      right={
        <div
          className="flex items-center gap-2.5 flex-wrap text-[11px]"
          style={{ color: "var(--text-secondary)" }}
        >
          {STATE_ORDER.map((cat) => (
            <span key={cat} className="flex items-center gap-1">
              <span
                className="inline-block w-3 h-3 rounded-sm"
                style={{ backgroundColor: STATE_COLORS[cat].color }}
              />
              {t(`state_${cat}`, STATE_COLORS[cat].label)}
            </span>
          ))}
        </div>
      }
    >
      <p className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>
        {t(
          "syncHint",
          "Move across the chart — the cursor reads every machine's state at that moment.",
        )}
      </p>

      <div className="relative overflow-x-auto">
        <div style={{ minWidth: 600 }}>
          {/* Time axis */}
          <div className="relative" style={{ marginLeft: LABEL_W, height: 18 }}>
            {ticks.map((tk) => (
              <span
                key={tk}
                className="absolute text-[10px] -translate-x-1/2"
                style={{
                  left: `${((tk - start) / span) * 100}%`,
                  color: "var(--text-secondary)",
                }}
              >
                {formatTimeOfDay(new Date(tk).toISOString())}
              </span>
            ))}
          </div>

          <div ref={barsRef} className="relative">
            {/* gridlines */}
            <div
              className="absolute top-0 bottom-0 pointer-events-none"
              style={{ left: LABEL_W, right: 0 }}
            >
              {ticks.map((tk) => (
                <div
                  key={tk}
                  className="absolute top-0 bottom-0 border-l"
                  style={{
                    left: `${((tk - start) / span) * 100}%`,
                    borderColor: "var(--border-primary)",
                    opacity: 0.4,
                  }}
                />
              ))}
            </div>

            {lanesOrdered.map((lane) => {
              const ivs = stateLanes.get(lane.sn) ?? [];
              const isFocused = focusedSn === lane.sn;
              const dimmed = focusedSn != null && !isFocused;
              const firstStart =
                ivs.length > 0 ? parseTs(ivs[0].state_start) : start;
              const leadGap =
                firstStart > start ? ((firstStart - start) / span) * 100 : 0;
              return (
                <div
                  key={lane.sn}
                  className="relative flex items-center transition-all duration-200"
                  style={{
                    height: LANE_H + LANE_GAP,
                    opacity: dimmed ? 0.4 : 1,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onPin?.(lane.sn)}
                    className="flex items-center gap-2 pr-3 shrink-0 text-left"
                    style={{ width: LABEL_W }}
                  >
                    <span
                      className="text-xs font-medium truncate"
                      style={{
                        color: isFocused
                          ? "var(--text-primary)"
                          : "var(--text-secondary)",
                      }}
                      title={lane.name}
                    >
                      {lane.name}
                    </span>
                  </button>

                  <div
                    className="relative flex-1 overflow-hidden rounded-lg"
                    style={{
                      height: LANE_H,
                      backgroundColor: "var(--bg-secondary)",
                    }}
                  >
                    {leadGap > 0 && (
                      <div
                        className="absolute top-0 bottom-0"
                        style={{
                          left: 0,
                          width: `${leadGap}%`,
                          background:
                            "repeating-linear-gradient(45deg, var(--border-primary), var(--border-primary) 5px, transparent 5px, transparent 10px)",
                          opacity: 0.5,
                        }}
                      />
                    )}
                    {ivs.map((iv, i) => {
                      const s = parseTs(iv.state_start);
                      const e = parseTs(iv.state_end);
                      if (Number.isNaN(s) || Number.isNaN(e)) return null;
                      const leftPct = ((s - start) / span) * 100;
                      const widthPct = Math.max(((e - s) / span) * 100, 0.15);
                      const col =
                        STATE_COLORS[iv.state_category as StateCategory];
                      return (
                        <div
                          key={i}
                          className="sa-st-bar absolute top-0 bottom-0"
                          style={{
                            left: `${leftPct}%`,
                            width: `${widthPct}%`,
                            transformOrigin: "left center",
                            background: `linear-gradient(180deg, ${col.color}, ${col.colorTo})`,
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Synchronized crosshair overlay (captures the mouse across all lanes) */}
            <div
              ref={overlayRef}
              className="absolute top-0 cursor-crosshair"
              style={{ left: LABEL_W, right: 0, height: tracksHeight }}
              onMouseMove={handleMove}
              onMouseLeave={handleLeave}
            >
              {cursor && (
                <div
                  className="absolute top-0 bottom-0 pointer-events-none"
                  style={{
                    left: `${cursor.frac * 100}%`,
                    width: 2,
                    backgroundColor: "#eab308",
                    boxShadow: "0 0 6px rgba(234,179,8,0.7)",
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Synchronized readout — all machines at the cursor instant */}
      {cursor && readout.length > 0 && (
        <div
          className="fixed z-50 pointer-events-none rounded-xl shadow-2xl border p-3 text-xs"
          style={{
            left: Math.min(cursor.clientX + 16, window.innerWidth - 260),
            top: Math.min(
              cursor.clientY + 16,
              window.innerHeight - 40 - readout.length * 22,
            ),
            width: 244,
            backgroundColor: "var(--bg-secondary)",
            borderColor: "var(--border-primary)",
            color: "var(--text-primary)",
          }}
        >
          <div
            className="font-semibold mb-1.5 tabular-nums"
            style={{ color: "#eab308" }}
          >
            🕒 {fmtMs(cursor.t)}
          </div>
          <div className="flex flex-col gap-1">
            {readout.map((r) => {
              const col = r.category ? STATE_COLORS[r.category] : null;
              return (
                <div key={r.sn} className="flex items-center gap-2">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
                    style={{
                      backgroundColor: col?.color ?? "var(--border-primary)",
                    }}
                  />
                  <span
                    className="truncate flex-1"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {r.name}
                  </span>
                  <span
                    className="shrink-0 font-medium"
                    style={{ color: col?.color ?? "var(--text-secondary)" }}
                  >
                    {r.category ? t(`state_${r.category}`, col!.label) : "—"}
                  </span>
                </div>
              );
            })}
          </div>
          {/* Active faults at the cursor (non-running machines) */}
          {readout.some((r) => r.fault) && (
            <div
              className="mt-2 pt-1.5 border-t"
              style={{ borderColor: "var(--border-primary)" }}
            >
              {readout
                .filter((r) => r.fault)
                .slice(0, 3)
                .map((r) => (
                  <div
                    key={r.sn}
                    className="truncate"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    <b style={{ color: "var(--text-primary)" }}>
                      {r.name.split(" ")[0]}
                    </b>
                    : {r.fault}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}
    </Panel>
  );
};

export default StatusTimeline;
